import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Select,
  Table,
  message,
  Space,
  Popconfirm,
  Modal,
  Divider,
  Typography,
  Spin,
  InputNumber,
  Switch,
  Row,
  Col,
  Input,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  REPORT_HEADER_FIELDS,
  DEFAULT_HEADER_CONFIG,
  ReportHeaderConfig,
} from '../../utils/reportHeaderFields';
import { generateReportHeader, fetchHeaderConfig, saveHeaderConfig } from '../../utils/reportHeaderGenerator';
import { makePostCall, clearHeaderConfigCache } from '../../utils/helper';

const { Title, Text } = Typography;
const { Option } = Select;

const ReportHeaderConfigPage: React.FC = () => {
  const [config, setConfig] = useState<ReportHeaderConfig>(DEFAULT_HEADER_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const savedConfig = await fetchHeaderConfig(makePostCall);
      if (savedConfig) {
        // Normalize empty strings to null
        const normalizedConfig = {
          ...savedConfig,
          rows: savedConfig.rows.map(row =>
            row.map(cell => ({
              ...cell,
              field: cell.field === '' ? null : cell.field,
            }))
          ),
        };
        setConfig(normalizedConfig);
      } else {
        setConfig(DEFAULT_HEADER_CONFIG);
      }
    } catch (error) {
      message.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    // Validate configuration
    if (!config.rows || config.rows.length === 0) {
      message.warning('Please add at least one row to the header');
      return;
    }

    // Check if all rows have at least 1 non-empty field
    const invalidRows = config.rows.filter(row => {
      const nonEmptyFields = row.filter(cell => cell.field !== null && cell.field !== undefined && cell.field.trim() !== '');
      return nonEmptyFields.length === 0;
    });
    if (invalidRows.length > 0) {
      message.warning('Each row must have at least one field selected');
      return;
    }

    // Ensure margins have default values if not set
    const configToSave = {
      ...config,
      margins: config.margins || {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
      },
      showHeaderFooterAllPages: config.showHeaderFooterAllPages !== undefined
        ? config.showHeaderFooterAllPages
        : true,
    };

    setSaving(true);
    try {
      const success = await saveHeaderConfig(makePostCall, configToSave);
      if (success) {
        clearHeaderConfigCache();
        message.success('Header configuration saved successfully. Please refresh the page to see changes in reports.');
      } else {
        message.error('Failed to save configuration');
      }
    } catch (error) {
      message.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setConfig(DEFAULT_HEADER_CONFIG);
    message.info('Reset to default configuration');
  };

  const handleAddRow = () => {
    setConfig({
      ...config,
      rows: [
        ...config.rows,
        [
          { label: 'Select Field', field: null },
          { label: 'Select Field', field: null },
        ],
      ],
    });
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newRows = config.rows.filter((_, index) => index !== rowIndex);
    setConfig({ ...config, rows: newRows });
  };

  const handleFieldChange = (rowIndex: number, cellIndex: number, fieldId: string | undefined) => {
    const newRows = [...config.rows];

    if (!fieldId) {
      // User cleared the field
      newRows[rowIndex][cellIndex] = {
        label: 'Select Field',
        field: null,
      };
    } else {
      // User selected a field
      const field = REPORT_HEADER_FIELDS.find(f => f.id === fieldId);
      if (!field) return;

      newRows[rowIndex][cellIndex] = {
        label: field.label,
        field: fieldId,
      };
    }

    setConfig({ ...config, rows: newRows });
  };

  const handleMarginChange = (side: 'top' | 'right' | 'bottom' | 'left', value: number | null) => {
    setConfig({
      ...config,
      margins: {
        ...config.margins,
        top: config.margins?.top || 20,
        right: config.margins?.right || 20,
        bottom: config.margins?.bottom || 20,
        left: config.margins?.left || 20,
        [side]: value || 0,
      },
    });
  };

  const handleShowHeaderFooterAllPagesChange = (checked: boolean) => {
    setConfig({
      ...config,
      showHeaderFooterAllPages: checked,
    });
  };

  const handleShowFooterChange = (checked: boolean) => {
    setConfig({
      ...config,
      showFooter: checked,
    });
  };

  const handleFooterTemplateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setConfig({
      ...config,
      footerTemplate: e.target.value,
    });
  };

  // Generate preview data
  const generatePreviewData = () => {
    const mockPatient = {
      pat_name: 'CH.SRI LAKSHMI BAI',
      pat_sex: 'F',
      pat_dob: '1980-05-15',
      pat_pin: 'DI_82',
      pat_admission_no: 'ADM001',
    };

    const mockOrder = {
      pacs_ord_id: 'ORD123',
      po_acc_no: '1510202512513990',
      po_ref_doc: 'Dr.LAVU HARISH GARU',
      po_modality: 'DX',
      po_req_time: '2025-10-15T00:00:00',
      po_diag_desc: 'X-Ray Chest PA View',
      po_clinical_history: 'Cough and fever',
    };

    const mockReport = {
      pr_status: 'SIGNEDOFF',
      created_at: '2025-11-18T16:40:57',
    };

    const mockWorkflow = {
      assigned_radiologist_name: 'Dr. SAMPLE RADIOLOGIST',
    };

    return { mockPatient, mockOrder, mockReport, mockWorkflow };
  };

  const handlePreview = () => {
    setPreviewVisible(true);
  };

  // Table columns for configuration display
  const columns = [
    {
      title: 'Row',
      dataIndex: 'rowIndex',
      key: 'rowIndex',
      width: 60,
      render: (text: any, record: any, index: number) => index + 1,
    },
    {
      title: 'Field 1 (Left)',
      key: 'field1',
      width: '40%',
      render: (text: any, record: any, rowIndex: number) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select a field"
          value={record[0]?.field === null ? undefined : record[0]?.field}
          onChange={(value) => handleFieldChange(rowIndex, 0, value)}
          showSearch
          optionFilterProp="children"
          allowClear
        >
          {REPORT_HEADER_FIELDS.map(field => (
            <Option key={field.id} value={field.id}>
              {field.label}
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                {field.description}
              </Text>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Field 2 (Right)',
      key: 'field2',
      width: '40%',
      render: (text: any, record: any, rowIndex: number) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select a field"
          value={record[1]?.field === null ? undefined : record[1]?.field}
          onChange={(value) => handleFieldChange(rowIndex, 1, value)}
          showSearch
          optionFilterProp="children"
          allowClear
        >
          {REPORT_HEADER_FIELDS.map(field => (
            <Option key={field.id} value={field.id}>
              {field.label}
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                {field.description}
              </Text>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (text: any, record: any, rowIndex: number) => (
        <Popconfirm
          title="Are you sure you want to delete this row?"
          onConfirm={() => handleDeleteRow(rowIndex)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const { mockPatient, mockOrder, mockReport, mockWorkflow } = generatePreviewData();
  const previewHtml = generateReportHeader({
    patient: mockPatient,
    order: mockOrder,
    report: mockReport,
    workflow: mockWorkflow,
    config,
  });

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={3}>Report Header Configuration</Title>
        <Text type="secondary">
          Configure the fields displayed in the report header. Each row can contain 1 or 2 fields.
          If only one field is selected in a row, it will take the full width.
          You can clear any field using the × button in the dropdown.
          The header will be displayed at the top of every report.
        </Text>

        <Divider />

        <Space style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveConfiguration}
            loading={saving}
          >
            Save Configuration
          </Button>
          <Button icon={<EyeOutlined />} onClick={handlePreview}>
            Preview
          </Button>
          <Popconfirm
            title="Are you sure you want to reset to default configuration?"
            onConfirm={handleResetToDefault}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<ReloadOutlined />}>Reset to Default</Button>
          </Popconfirm>
        </Space>

        <Spin spinning={loading}>
          {/* Header Fields Configuration */}
          <Title level={5} style={{ marginTop: '16px' }}>Header Fields</Title>
          <Table
            columns={columns}
            dataSource={config.rows}
            pagination={false}
            rowKey={(record, index) => `row-${index}`}
            bordered
          />

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddRow}
            style={{ width: '100%', marginTop: '16px' }}
          >
            Add Row
          </Button>

          <Divider />

          {/* Page Margins Configuration */}
          <Title level={5}>Page Margins (mm)</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
            Configure the margins for the report pages in millimeters.
          </Text>
          <Row gutter={16}>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Top:</Text>
              </div>
              <InputNumber
                value={config.margins?.top || 20}
                onChange={(value) => handleMarginChange('top', value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Right:</Text>
              </div>
              <InputNumber
                value={config.margins?.right || 20}
                onChange={(value) => handleMarginChange('right', value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Bottom:</Text>
              </div>
              <InputNumber
                value={config.margins?.bottom || 20}
                onChange={(value) => handleMarginChange('bottom', value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Left:</Text>
              </div>
              <InputNumber
                value={config.margins?.left || 20}
                onChange={(value) => handleMarginChange('left', value)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Divider />

          {/* Header/Footer Display Configuration */}
          <Title level={5}>Header & Footer Display</Title>
          <div style={{ marginBottom: '16px' }}>
            <Space direction="vertical">
              <div>
                <Switch
                  checked={config.showHeaderFooterAllPages !== false}
                  onChange={handleShowHeaderFooterAllPagesChange}
                />
                <Text style={{ marginLeft: '12px' }}>
                  Show header & footer on all pages
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                When disabled, header appears on the first page only and footer appears at the end of the document only.
              </Text>
            </Space>
          </div>

          <Divider />

          {/* Footer Template Configuration */}
          <Title level={5}>Footer Configuration</Title>
          <div style={{ marginBottom: '16px' }}>
            <Space direction="vertical">
              <div>
                <Switch
                  checked={config.showFooter !== false}
                  onChange={handleShowFooterChange}
                />
                <Text style={{ marginLeft: '12px' }}>
                  Enable footer in reports
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                When enabled, a footer section will be added to reports.
              </Text>
            </Space>
          </div>

          {config.showFooter !== false && (
            <>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                Configure the default footer text that will appear at the bottom of all reports. This text will be editable in the report editor.
              </Text>
              <Input.TextArea
                value={config.footerTemplate || ''}
                onChange={handleFooterTemplateChange}
                placeholder="Enter footer template text..."
                rows={4}
                style={{ marginBottom: '16px' }}
              />
            </>
          )}
        </Spin>

        <Divider />

        <div style={{ marginTop: '16px' }}>
          <Title level={5}>Available Fields:</Title>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {REPORT_HEADER_FIELDS.map(field => (
              <div
                key={field.id}
                style={{
                  padding: '8px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                <Text strong>{field.label}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {field.description}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Modal
        title="Header Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Preview of the header with sample data. This is how it will appear in reports.
          </Text>
        </div>
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Modal>
    </div>
  );
};

export default ReportHeaderConfigPage;
